import { z } from 'zod';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { WorkflowAIInterviewerV2WriteBackStrategy } from 'libs/end-to-end/src/types/databases/workflows/ai-interviewer-v2-write-back/workflow-ai-interviewer-v2-write-back';
import { WorkflowHiringReadinessScoreWriteBackStrategy } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { WorkflowHiringReadinessExplanationWriteBackStrategy } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-explanation-write-back/workflow-hiring-readiness-explanation-write-back';
import { WriteBackToLeverStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/lever-write-back';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { CompositeDataSetAiInterviewerRubric } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';

export const MergeRealTimeWritebackConfig = z.object({
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  databaseId: z.string(),
  orgDbCompositeId: z.string(),
  dataSetId: z.string(),
  linkedAccountId: z.string(),
  aiInterviewerConfig: z
    .object({
      openAiModel: AllowedOpenAiModels.default('gpt-4o-mini'),
      rubricsByJobTitle: z
        .array(CompositeDataSetAiInterviewerRubric)
        .nullable()
        .default([]),
    })
    .nullable(),
  aiInterviewerWritebackConfig: z
    .object({
      strategy: WorkflowAIInterviewerV2WriteBackStrategy,
      mode: WorkflowWriteBackMode,
    })
    .nullable(),
  resumeParserEnabled: z.boolean(),
  resumeGradingConfig: z
    .object({
      openAiModel: AllowedOpenAiModels.default('gpt-4o-mini'),
      rubricsByJobTitle: z
        .array(CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric)
        .nullable()
        .default([]),
    })
    .nullable(),
  resumeGradingWritebackConfig: z
    .object({
      strategy: WriteBackToLeverStrategy,
      mode: WorkflowWriteBackMode,
      explanationsEnabled: z.boolean().optional(),
    })
    .nullable(),
  hiringReadinessWritebackConfig: z
    .object({
      strategy: WorkflowHiringReadinessScoreWriteBackStrategy,
      mode: WorkflowWriteBackMode,
    })
    .nullable(),
  // These fields below are optional because it was added after the initial DynamoDB table creation.
  hiringReadinessExplanationWritebackConfig: z
    .object({
      strategy: WorkflowHiringReadinessExplanationWriteBackStrategy,
      mode: WorkflowWriteBackMode,
    })
    .optional(),
});

// By the time we reach the state machine, we do not want undefineds, only nulls.
// This ensures we don't have any sort of edge cases around the accessing of sub-properties.
export const MergeRealTimeWritebackConfigWithNullsInstead =
  MergeRealTimeWritebackConfig.extend({
    hiringReadinessExplanationWritebackConfig: z
      .object({
        strategy: WorkflowHiringReadinessExplanationWriteBackStrategy,
        mode: WorkflowWriteBackMode,
      })
      .nullable(),
  });

export type MergeRealTimeWritebackConfigWithNullsInstead = z.infer<
  typeof MergeRealTimeWritebackConfigWithNullsInstead
>;

export type MergeRealTimeWritebackConfig = z.infer<
  typeof MergeRealTimeWritebackConfig
>;

export const OrgDBScopedMergeRealTimeWritebackConfig =
  MergeRealTimeWritebackConfig.omit({
    organizationId: true,
    databaseId: true,
    orgDbCompositeId: true,
  });

export type OrgDBScopedMergeRealTimeWritebackConfig = z.infer<
  typeof OrgDBScopedMergeRealTimeWritebackConfig
>;

export const OrgDBScopedWriteableMergeRealTimeWritebackConfig =
  OrgDBScopedMergeRealTimeWritebackConfig.omit({
    id: true,
  });

export type OrgDBScopedWriteableMergeRealTimeWritebackConfig = z.infer<
  typeof OrgDBScopedWriteableMergeRealTimeWritebackConfig
>;
